import type * as React from 'react'
import { forwardRef, useState } from 'react'
import type { JSX } from 'react'
import { clsx } from 'clsx'
import styles from './Image.module.css'

interface Source {
  /** Set of image source URLs along with their respective media conditions */
  srcSet: string
  /** Media condition that dictates when each source set is used */
  media: string
  /** Image slot size used by the browser when srcSet uses width descriptors */
  sizes?: string
  /** MIME type of the image source, e.g., "image/webp", "image/png" */
  type?: string
}

export interface ImageProps extends Omit<
  React.ImgHTMLAttributes<HTMLImageElement>,
  'srcSet'
> {
  /** Primary image source URL */
  src: string
  /** Array of source objects for responsive images */
  srcSet?: Source[]
  /** Text description of the image for accessibility */
  alt: string
  /** Placeholder image displayed while the primary image is loading */
  placeholder?: string
  /** Data-testid attribute for testing purposes */
  'data-testid'?: string
}

interface ImageCssProperties extends React.CSSProperties {
  '--image-placeholder'?: string
}

export const Image = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      src,
      srcSet,
      alt,
      placeholder,
      className,
      style,
      'data-testid': testId,
      ...props
    }: ImageProps,
    ref,
  ): JSX.Element => {
    const [loaded, setLoaded] = useState(false)

    const imageStyle: ImageCssProperties = {
      ...style,
      '--image-placeholder':
        placeholder && !loaded ? `url(${placeholder})` : undefined,
    }

    return (
      <picture data-testid={testId}>
        {srcSet?.map((source) => (
          <source key={`${source.srcSet}-${source.media}`} {...source} />
        ))}
        <img
          ref={ref}
          src={src}
          alt={alt}
          className={clsx(styles.image, className)}
          style={imageStyle}
          onLoad={() => setLoaded(true)}
          {...props}
        />
      </picture>
    )
  },
)

Image.displayName = 'Image'
